/**
 * ImageRegeneratePopover — bubble-menu control that regenerates the selected
 * image with AI.
 *
 * Self-contained: it renders its own trigger button (styled like the other
 * image bubble-menu buttons) and its own popover (styled like the Image URL /
 * ImageDetailsPopover popovers). On open the "Describe the image" textarea is
 * prefilled from the selected image's alt (falling back to its caption). The
 * user picks a style and generates; the result replaces the selected image
 * node's src/alt in place and marks it AI-generated.
 *
 * This is an INLINE image regenerate — it never passes a contentId, so the
 * backend won't persist a hero placeholder against the article.
 */

import { useState } from 'react';
import type { Editor } from '@tiptap/react';
import { Loader2, Sparkles } from 'lucide-react';

import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTenantStore } from '@/stores/useTenantStore';
import { editorAIService } from '@/services/editorAIService';

type ImageStyle = 'modern' | 'minimalist' | 'vibrant' | 'professional';

const STYLE_OPTIONS: { value: ImageStyle; label: string }[] = [
  { value: 'modern', label: 'Modern' },
  { value: 'minimalist', label: 'Minimalist' },
  { value: 'vibrant', label: 'Vibrant' },
  { value: 'professional', label: 'Professional' },
];

export interface ImageRegeneratePopoverProps {
  editor: Editor;
  focusTerms?: string[];
}

/** Text of the first heading node in the document, or '' if none. */
function firstHeadingText(editor: Editor): string {
  let title = '';
  editor.state.doc.descendants((node) => {
    if (title) return false;
    if (node.type.name === 'heading') {
      title = node.textContent;
      return false;
    }
    return true;
  });
  return title;
}

export function ImageRegeneratePopover({
  editor,
  focusTerms,
}: ImageRegeneratePopoverProps) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState<ImageStyle>('professional');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpenChange = (nextOpen: boolean) => {
    // Never close mid-generation — the request keeps running and the popover
    // shows its spinner until it resolves.
    if (!nextOpen && isGenerating) return;
    if (nextOpen) {
      const attrs = editor.getAttributes('image');
      const alt = typeof attrs.alt === 'string' ? attrs.alt.trim() : '';
      const caption =
        typeof attrs.caption === 'string' ? attrs.caption.trim() : '';
      setPrompt(alt || caption || '');
      setStyle('professional');
      setError(null);
    }
    setOpen(nextOpen);
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    const attrs = editor.getAttributes('image');
    const existingAlt = typeof attrs.alt === 'string' ? attrs.alt : '';
    const trimmedPrompt = prompt.trim();
    const tenantId = useTenantStore.getState().currentTenant || undefined;
    try {
      const result = await editorAIService.generateHeroImage(
        {
          title: firstHeadingText(editor),
          keyword: focusTerms?.[0] ?? '',
          style,
          // Omit when empty so the backend falls back to title/keyword building.
          prompt: trimmedPrompt || undefined,
          // Intentionally no contentId: inline regenerate must not persist a
          // hero placeholder against the article.
        },
        tenantId
      );
      editor
        .chain()
        .focus()
        .updateAttributes('image', {
          src: result.image_url,
          alt: result.alt_text || existingAlt,
          isAIGenerated: true,
        })
        .run();
      setOpen(false);
    } catch (err) {
      console.error('[ImageRegeneratePopover] generate failed:', err);
      setError('Could not generate an image. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="p-1.5 rounded transition-colors text-slate-600 hover:bg-slate-100"
          title="Regenerate with AI"
        >
          <Sparkles className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="start">
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="image-regenerate-prompt" className="text-xs font-medium">
              Describe the image
            </Label>
            <Textarea
              id="image-regenerate-prompt"
              rows={3}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe the image you want to generate…"
              className="text-sm resize-none"
              disabled={isGenerating}
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs font-medium">Style</Label>
            <Select
              value={style}
              onValueChange={(next) => setStyle(next as ImageStyle)}
              disabled={isGenerating}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STYLE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {error ? (
            <p role="alert" className="text-xs text-destructive">
              {error}
            </p>
          ) : null}

          <button
            type="button"
            onClick={handleGenerate}
            disabled={isGenerating}
            className="flex w-full items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-coral rounded hover:bg-coral-hover transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Generate image
              </>
            )}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default ImageRegeneratePopover;
